import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Subscribe & Save checkbox
 *
 * Opt-in control above Add to cart. When checked, the variant's selling plan is
 * written into the product form so the add becomes a subscription. Unchecking
 * removes the field so the add stays a one-time purchase.
 *
 * Allocations belong to the variant, so prices and the plan id are re-read from
 * the payload whenever the selected variant changes. First order (Today) uses the
 * bundle total (12% / 17% off for 2 / 3 jars) plus the selling-plan rate. Recurring
 * charges (Then) use only the selling-plan rate times quantity — the bundle
 * amount-off is not applied again.
 */
class SubscriptionCheckbox extends HTMLElement {
  /** @type {Record<string, {required?: boolean, plans?: Array<Record<string, any>>}>} */
  #data = {};

  /** @type {Element | null} */
  #section = null;

  /** @type {MutationObserver | null} */
  #variantObserver = null;

  /** @type {boolean} */
  #syncQueued = false;

  connectedCallback() {
    this.#readPayload();
    this.addEventListener('change', this.#onChange);

    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#section?.addEventListener('change', this.#onSectionChange);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
    document.addEventListener('submit', this.#onSubmit, true);

    this.#watchVariant();
    this.#restoreChecked();
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#section?.removeEventListener('change', this.#onSectionChange);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
    document.removeEventListener('submit', this.#onSubmit, true);
    this.#variantObserver?.disconnect();
    this.#variantObserver = null;

    const input = this.#variantInput();
    input?.removeEventListener('change', this.#sync);
  }

  #readPayload() {
    const json = this.querySelector('script[type="application/json"]');
    if (!json) {
      this.#data = {};
      return;
    }

    try {
      this.#data = JSON.parse(json.textContent || '{}') || {};
    } catch {
      this.#data = {};
    }
  }

  /** @returns {HTMLFormElement | null} */
  #form() {
    return (
      this.closest('form') ||
      /** @type {HTMLFormElement | null} */ (document.getElementById('gm-buy-form')) ||
      /** @type {HTMLFormElement | null} */ (
        this.closest('.shopify-section')?.querySelector('form[data-type="add-to-cart-form"]')
      )
    );
  }

  /** @returns {HTMLElement | null} */
  #host() {
    return this.closest('product-form-component') || this.#form();
  }

  /** @returns {HTMLInputElement | null} */
  #checkbox() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('.fcl-subscribe__input'));
  }

  /** @returns {HTMLInputElement | null} */
  #variantInput() {
    return /** @type {HTMLInputElement | null} */ (this.#form()?.querySelector('input[name="id"]'));
  }

  #watchVariant() {
    const input = this.#variantInput();
    if (!input) return;

    input.addEventListener('change', this.#sync);
    this.#variantObserver = new MutationObserver(this.#sync);
    this.#variantObserver.observe(input, { attributes: true, attributeFilter: ['value'] });
  }

  #restoreChecked() {
    const host = this.#host();
    const checkbox = this.#checkbox();
    if (!host || !checkbox || checkbox.disabled) return;
    if (host.dataset.subscriptionChecked === 'true') checkbox.checked = true;
  }

  #variantId() {
    return this.#variantInput()?.value || '';
  }

  #plan() {
    return this.#data[this.#variantId()]?.plans?.[0] ?? null;
  }

  /** @returns {number} */
  #quantity() {
    const form = this.#form();
    const gummies = form?.querySelector('input[name="gm-bundle"]:checked');
    const fromGummies = Number(gummies?.dataset?.quantity || gummies?.value);
    if (Number.isFinite(fromGummies) && fromGummies > 0) return fromGummies;

    const field = Number(form?.querySelector('input[name="quantity"]')?.value);
    if (Number.isFinite(field) && field > 0) return field;

    const bundle = this.#section?.querySelector(
      '.bundle-option--selected input[data-quantity], input[name="gm-bundle"]:checked'
    );
    const fromBundle = Number(bundle?.dataset?.quantity);
    if (Number.isFinite(fromBundle) && fromBundle > 0) return fromBundle;

    return 1;
  }

  #scheduleSync = () => {
    if (this.#syncQueued) return;
    this.#syncQueued = true;
    queueMicrotask(() => {
      this.#syncQueued = false;
      if (!this.isConnected) return;
      this.#sync();
    });
  };

  /**
   * Fixed amount-off already baked into the 2-jar / 3-jar one-time totals.
   * Same cents as `blocks/_bundle-option.liquid` and `sections/gummies-hero.liquid`.
   * @param {number} qty
   * @returns {number}
   */
  #bundleAmountOff(qty) {
    if (qty === 2) return 995;
    if (qty === 3) return 1990;
    if (qty > 3) return (qty - 1) * 995;
    return 0;
  }

  /**
   * Today: bundle total + subscription rate.
   * Then: subscription unit × quantity only (no bundle amount-off).
   * @param {{ planPrice?: number, thenPrice?: number, today?: string, then?: string }} plan
   * @returns {{ today: string, then: string, compare: string }}
   */
  #pricedPlan(plan) {
    const variant = this.#data[this.#variantId()];
    const unit = Number(variant?.price);
    const qty = this.#quantity();
    const planUnit = Number(plan.planPrice);
    const thenUnit = Number(plan.thenPrice ?? plan.planPrice);
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || '';

    if (!Number.isFinite(unit) || unit <= 0 || !Number.isFinite(planUnit)) {
      return {
        today: plan.today || '',
        then: plan.then || plan.today || '',
        compare: '',
      };
    }

    const oneTime = unit * qty - this.#bundleAmountOff(qty);
    const todayCents = Math.round((oneTime * planUnit) / unit);
    const thenCents = Number.isFinite(thenUnit) ? thenUnit * qty : todayCents;

    return {
      today: formatMoney(todayCents, format, currency),
      then: formatMoney(thenCents, format, currency),
      compare: oneTime > todayCents ? formatMoney(oneTime, format, currency) : '',
    };
  }

  #onQuantityUpdate = (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;
    const inForm = Boolean(this.#form()?.contains(target));
    const inSection = Boolean(this.#section?.contains(target));
    if (!inForm && !inSection) return;
    this.#scheduleSync();
  };

  #sync = () => {
    if (this.#section?.querySelector('subscribe-save[data-update-form]')) {
      this.hidden = true;
      this.#writeSellingPlan(null);
      return;
    }

    const variant = this.#data[this.#variantId()];
    const plan = variant?.plans?.[0];
    const checkbox = this.#checkbox();

    if (!plan || !checkbox) {
      this.hidden = true;
      this.#writeSellingPlan(null);
      this.#announce();
      return;
    }

    this.hidden = false;
    checkbox.disabled = Boolean(variant.required);
    if (variant.required) checkbox.checked = true;

    const priced = this.#pricedPlan(plan);

    this.#setText('[data-subscribe-today]', priced.today);
    this.#setText('[data-subscribe-then]', priced.then);
    this.#setText('[data-subscribe-frequency]', plan.frequency);

    this.classList.toggle('is-checked', checkbox.checked);
    this.dataset.sellingPlan = String(plan.id || '');
    this.dataset.todayPrice = priced.today || '';
    this.dataset.comparePrice = priced.compare || '';

    this.#writeSellingPlan(checkbox.checked ? String(plan.id) : null);
    this.#announce();
  };

  /**
   * @param {string} selector
   * @param {string} value
   */
  #setText(selector, value) {
    const node = this.querySelector(selector);
    if (node && value) node.textContent = value;
  }

  /** @param {string | null} sellingPlanId */
  #writeSellingPlan(sellingPlanId) {
    const form = this.#form();
    if (!form) return;

    let field = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="selling_plan"]'));

    if (!sellingPlanId) {
      field?.remove();
      return;
    }

    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'selling_plan';
      form.appendChild(field);
    }

    field.value = sellingPlanId;
  }

  #onChange = () => {
    const host = this.#host();
    const checkbox = this.#checkbox();
    if (host && checkbox && !checkbox.disabled) {
      host.dataset.subscriptionChecked = checkbox.checked ? 'true' : 'false';
    }
    this.#sync();
  };

  #onSectionChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (this.contains(target)) return;
    this.#scheduleSync();
  };

  #onSubmit = (event) => {
    if (event.target === this.#form()) this.#sync();
  };

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const next = detail?.html?.querySelector(`subscription-checkbox[data-section-id="${this.dataset.sectionId}"] script[type="application/json"]`);
        if (next) {
          try {
            this.#data = JSON.parse(next.textContent || '{}') || this.#data;
          } catch {
            // Keep the payload we already have.
          }
        }
        this.#restoreChecked();
        this.#sync();
      })
      .catch(() => {
        this.#sync();
      });
  };

  #announce() {
    const plan = this.#plan();
    const checked = Boolean(this.#checkbox()?.checked);
    const priced = plan ? this.#pricedPlan(plan) : { today: '', compare: '' };

    this.dispatchEvent(
      new CustomEvent('subscribe-save:change', {
        bubbles: true,
        detail: {
          sellingPlan: checked ? plan?.id ?? null : null,
          price: priced.today,
          comparePrice: priced.compare,
        },
      })
    );
  }
}

if (!customElements.get('subscription-checkbox')) {
  customElements.define('subscription-checkbox', SubscriptionCheckbox);
}
